import { Check, Clock3, Info } from 'lucide-react';
import { useSearchParams } from 'react-router-dom';
import whatsappIcon from '@/assets/whatsapp.png';
import MusicRegistrationHeader from '@/components/Layout/MusicRegistrationHeader';
import type { MusicRegistrationCompletionStatus } from '@/types/musicRegistrationFlow';

const completionContent: Record<MusicRegistrationCompletionStatus, {
  title: string;
  message: string;
  tone: 'success' | 'pending' | 'info';
}> = {
  'tutor-submitted': {
    title: 'Registration complete',
    message: 'Your registration has been successfully submitted.',
    tone: 'success',
  },
  'parent-submitted': {
    title: 'Request submitted',
    message: 'Your Music Tutor request has been successfully submitted.',
    tone: 'success',
  },
  'tutor-approved': {
    title: 'Your Music Tutor profile is already approved',
    message: 'This account already has an active Music Tutor profile. No additional registration is required.',
    tone: 'success',
  },
  'tutor-pending': {
    title: 'Your Music Tutor profile is already under review',
    message: 'This account already has a Music Tutor profile pending review. No additional submission is required.',
    tone: 'pending',
  },
  'session-unavailable': {
    title: 'This registration session is unavailable',
    message: 'The authentication page must be opened from an active Music registration form.',
    tone: 'info',
  },
};

const isCompletionStatus = (value: string | null): value is MusicRegistrationCompletionStatus =>
  value !== null && Object.prototype.hasOwnProperty.call(completionContent, value);

const MusicRegistrationCompletePage = () => {
  const [searchParams] = useSearchParams();
  const statusParam = searchParams.get('status');
  const status: MusicRegistrationCompletionStatus = isCompletionStatus(statusParam)
    ? statusParam
    // Keep the page easy to preview locally, without exposing the successful
    // completion state through an unqualified URL in production.
    : import.meta.env.DEV && statusParam === null
      ? 'tutor-submitted'
    : 'session-unavailable';
  const content = completionContent[status];
  const Icon = content.tone === 'success' ? Check : content.tone === 'pending' ? Clock3 : Info;
  const iconClasses = content.tone === 'success'
    ? 'bg-primary text-on-primary'
    : content.tone === 'pending'
      ? 'bg-amber-50 text-amber-600'
      : 'bg-primary-50 text-primary';
  const isSubmitted = status === 'tutor-submitted' || status === 'parent-submitted';
  const whatsappMessage = encodeURIComponent(
    'Hello! I have completed my Music registration and would like to ask a question.',
  );

  return (
    <div className="theme-music min-h-screen bg-[#fffdf9] font-redesign-body text-gray-900">
      <MusicRegistrationHeader />
      <main className="flex min-h-[calc(100vh-5rem)] items-center px-4 py-10 sm:px-6 sm:py-16 lg:px-8">
        <section
          aria-labelledby="music-registration-result-title"
          className="mx-auto w-full max-w-xl rounded-3xl border border-primary-100 bg-white px-6 py-10 text-center shadow-[0_24px_70px_-36px_rgb(var(--brand-primary)_/_0.35)] sm:px-10 sm:py-14"
        >
          <div className={`mx-auto flex h-16 w-16 items-center justify-center rounded-full ${iconClasses}`}>
            <Icon className="h-8 w-8" strokeWidth={2.5} aria-hidden="true" />
          </div>
          <h1 id="music-registration-result-title" className="mt-6 text-2xl font-semibold tracking-tight text-gray-900 sm:text-3xl">
            {content.title}
          </h1>
          <p className="mx-auto mt-4 max-w-md text-base leading-7 text-gray-600">
            {content.message}
          </p>
          {isSubmitted && (
            <div className="mx-auto mt-7 max-w-md space-y-3 text-sm leading-6 text-gray-600 sm:text-base">
              <p>{status === 'tutor-submitted' ? <>
                Our team will complete the verification and matching process within{' '}
                <strong className="font-semibold text-gray-900">5–7 working days</strong>.
              </> : <>
                Our team will review your request and begin matching within{' '}
                <strong className="font-semibold text-gray-900">5–7 working days</strong>.
              </>}</p>
              <p>
                A dedicated team member will contact you via WhatsApp with updates and the next steps.
              </p>
            </div>
          )}
          <a
            href={`https://wa.me/601112049396?text=${whatsappMessage}`}
            target="_blank"
            rel="noreferrer"
            className="mx-auto mt-8 inline-flex min-h-12 items-center justify-center gap-2 rounded-xl bg-[#25D366] px-6 py-3 text-base font-semibold text-white transition-colors hover:bg-[#1ebe5d] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#25D366] focus-visible:ring-offset-2"
          >
            <img src={whatsappIcon} alt="" className="h-5 w-5" aria-hidden="true" />
            Contact us on WhatsApp
          </a>
        </section>
      </main>
    </div>
  );
};

export default MusicRegistrationCompletePage;
